import React from 'react';
import styled from 'styled-components';
import DateTimePicker from 'components/DateTimePicker';
import Card from 'components/Card';
import ComponentDemo from '../../components/ComponentDemo';
import CodeBlock from '../../components/CodeBlock';

const PageHeader = styled.div`
  margin-bottom: 48px;
`;

const Title = styled.h1`
  font-size: 36px;
  font-weight: 700;
  color: ${(props) => (props.theme.name === 'dark' ? '#ffffff' : '#0F172A')};
  margin: 0 0 12px 0;
`;

const Subtitle = styled.p`
  font-size: 18px;
  color: ${(props) => (props.theme.name === 'dark' ? '#94A3B8' : '#64748B')};
  margin: 0;
  line-height: 1.6;
`;

const Section = styled.section`
  margin-bottom: 64px;
`;

const SectionTitle = styled.h2`
  font-size: 24px;
  font-weight: 600;
  color: ${(props) => (props.theme.name === 'dark' ? '#ffffff' : '#0F172A')};
  margin: 0 0 8px 0;
`;

const SectionDescription = styled.p`
  font-size: 15px;
  color: ${(props) => (props.theme.name === 'dark' ? '#94A3B8' : '#64748B')};
  line-height: 1.7;
  margin: 0 0 32px 0;
`;

const DateTimePickerDocs = () => {
  const [dateValue, setDateValue] = React.useState('');
  const [dateTimeValue, setDateTimeValue] = React.useState(null);

  return (
    <div>
      <PageHeader>
        <Title>Date & Time Picker</Title>
        <Subtitle>
          A fully-featured date and time picker component with theme support, responsive design,
          keyboard input, and flexible configuration. Supports date-only and date+time modes with
          timezone-safe handling.
        </Subtitle>
      </PageHeader>

      <Section>
        <SectionTitle>Basic Usage</SectionTitle>
        <SectionDescription>
          The DateTimePicker component can be used for date selection or combined date and time selection.
          You can type dates directly (e.g., "1/1/2024" or "10/15/2025") or click the calendar icon to select visually.
        </SectionDescription>

        <ComponentDemo label="Date Only Picker (Type or Click)" align="stretch">
          <div style={{ width: '400px', maxWidth: '100%' }}>
            <DateTimePicker
              value={dateValue}
              onChange={setDateValue}
              showTime={false}
              placeholder="MM/DD/YYYY"
              cancelText="Clear"
            />
            {dateValue && (
              <div style={{ marginTop: '12px', fontSize: '13px', color: '#64748b' }}>
                Selected: {dateValue}
              </div>
            )}
          </div>
        </ComponentDemo>

        <ComponentDemo label="Date and Time Picker" align="stretch">
          <div style={{ width: '400px', maxWidth: '100%' }}>
            <DateTimePicker
              value={dateTimeValue}
              onChange={setDateTimeValue}
              showTime={true}
              confirmText="Set Schedule"
              minDate={new Date()}
            />
            {dateTimeValue && (
              <div style={{ marginTop: '12px', fontSize: '13px', color: '#64748b' }}>
                Selected: {dateTimeValue.toLocaleString()}
              </div>
            )}
          </div>
        </ComponentDemo>

        <CodeBlock>
{`import DateTimePicker from 'components/DateTimePicker';

// Date only (returns "YYYY-MM-DD" string)
const [date, setDate] = useState('');

<DateTimePicker
  value={date}
  onChange={setDate}
  showTime={false}
  placeholder="MM/DD/YYYY"
  cancelText="Clear"
/>

// Date and time (returns Date object)
const [dateTime, setDateTime] = useState(null);

<DateTimePicker
  value={dateTime}
  onChange={setDateTime}
  showTime={true}
  confirmText="Set Schedule"
  minDate={new Date()}
/>`}
        </CodeBlock>
      </Section>

      <Section>
        <SectionTitle>Features</SectionTitle>
        <SectionDescription>
          Key features of the DateTimePicker component.
        </SectionDescription>

        <Card title="Component Features" headerBorder>
          <div style={{ fontSize: '14px', lineHeight: '1.8' }}>
            • <strong>Typed Input:</strong> Type dates directly (e.g., "1/1/2024") with real-time parsing and validation
            <br />
            • <strong>Visual Calendar:</strong> Month-based calendar with navigation and clickable dates
            <br />
            • <strong>Smart Date Parsing:</strong> Handles multiple formats (M/D/YYYY, MM/DD/YY, etc.)
            <br />
            • <strong>Timezone Safe:</strong> Avoids timezone conversion bugs by using local noon for date-only values
            <br />
            • <strong>Time Selection:</strong> Optional hour and minute selection with AM/PM for date-time mode
            <br />
            • <strong>Visual Feedback:</strong> Green border for valid dates, red for invalid while typing
            <br />
            • <strong>Responsive Design:</strong> Optimized for mobile with full-screen modal on small screens
            <br />
            • <strong>Theme Support:</strong> Automatically adapts to light and dark themes
            <br />
            • <strong>Smart Positioning:</strong> Dropdown automatically positions above or below input based on available space
            <br />
            • <strong>Date Validation:</strong> Supports min/max date constraints
            <br />
            • <strong>Flexible Output:</strong> Returns strings for date-only, Date objects for date-time
          </div>
        </Card>
      </Section>

      <Section>
        <SectionTitle>Props</SectionTitle>
        <Card headerBorder title="DateTimePicker Props">
          <div style={{ fontSize: '14px', lineHeight: '1.8' }}>
            • <strong>value</strong> (Date | string | null): Current selected date/time value. Accepts Date objects or "YYYY-MM-DD"/"MM/DD/YYYY" strings
            <br />
            • <strong>onChange</strong> (function): Callback when date/time changes. Receives string for date-only, Date object for date-time
            <br />
            • <strong>onConfirm</strong> (function, optional): Callback when confirm button clicked (date-time mode only)
            <br />
            • <strong>onCancel</strong> (function, optional): Callback when cancel button clicked
            <br />
            • <strong>showTime</strong> (boolean): Whether to show time selection (default: true)
            <br />
            • <strong>minDate</strong> (Date | string | null): Minimum selectable date (default: null - no restriction)
            <br />
            • <strong>maxDate</strong> (Date | string | null): Maximum selectable date (default: null)
            <br />
            • <strong>placeholder</strong> (string): Placeholder text for the input (default: "Select date and time" or "MM/DD/YYYY")
            <br />
            • <strong>confirmText</strong> (string): Text for confirm button (default: "Select")
            <br />
            • <strong>cancelText</strong> (string): Text for cancel button (default: "Cancel")
          </div>
        </Card>
      </Section>

      <Section>
        <SectionTitle>Usage Examples</SectionTitle>
        <CodeBlock>
{`// Customer filter (date only, any date allowed)
const [customerSince, setCustomerSince] = useState('');

<DateTimePicker
  value={customerSince}
  onChange={setCustomerSince}
  showTime={false}
  placeholder="Start date"
  cancelText="Clear"
/>
// onChange receives: "2024-01-01" string

// Schedule message (date+time, future only)
const [scheduledTime, setScheduledTime] = useState(null);

<DateTimePicker
  value={scheduledTime}
  onChange={setScheduledTime}
  showTime={true}
  confirmText="Schedule"
  minDate={new Date()}
/>
// onChange receives: Date object

// Date range filter (with min constraint)
<DateTimePicker
  value={endDate}
  onChange={setEndDate}
  showTime={false}
  minDate={startDate}
  cancelText="Clear"
/>

// Accepts both formats as value
<DateTimePicker
  value="2024-01-15"  // String
  // OR
  value={new Date(2024, 0, 15)}  // Date object
  onChange={handleChange}
  showTime={false}
/>`}
        </CodeBlock>
      </Section>

      <Section>
        <SectionTitle>Best Practices</SectionTitle>
        <ComponentDemo direction="column" gap="16px">
          <div style={{ fontSize: '14px', lineHeight: '1.8', color: 'inherit' }}>
            {/* eslint-disable-next-line jsx-a11y/accessible-emoji */}
            <strong>✅ Do:</strong>
            <br />
            • Use showTime=false for date-only fields (filters, DOB, etc.)
            <br />
            • Set minDate only when needed (defaults to null for flexibility)
            <br />
            • Accept both typed input and calendar clicks
            <br />
            • Store date-only values as strings ("YYYY-MM-DD") to avoid timezone issues
            <br />
            • Use custom confirmText/cancelText for clarity ("Clear", "Schedule", etc.)
            <br />
            • Test typing dates like "1/1/2024" to ensure parsing works
            <br />
            <br />
            {/* eslint-disable-next-line jsx-a11y/accessible-emoji */}
            <strong>❌ Don't:</strong>
            <br />
            • Don't use showTime=true when time isn't needed
            <br />
            • Don't convert date strings with new Date(string) - causes timezone bugs
            <br />
            • Don't set minDate={`{new Date()}`} for historical date filters
            <br />
            • Don't use .toISOString() for date-only values (use the string onChange returns)
            <br />
            • Don't forget the component handles both string and Date values automatically
          </div>
        </ComponentDemo>
      </Section>
    </div>
  );
};

export default DateTimePickerDocs;